import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Files, FilePdf, FileImage, FileText, MagnifyingGlass, UploadSimple, Plus, LinkBreak, ArrowSquareOut,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { dmsApi, STATUS_STYLE, fmtDate, fmtSize } from './dmsApi';

const docIcon = (mime, size = 20) => {
  if (mime === 'application/pdf') return <FilePdf size={size} weight="duotone" className="text-rose-500 shrink-0" />;
  if ((mime || '').startsWith('image/')) return <FileImage size={size} weight="duotone" className="text-sky-500 shrink-0" />;
  return <FileText size={size} weight="duotone" className="text-zinc-400 shrink-0" />;
};

function AttachDialog({ open, onClose, targetType, targetId, onDone }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      dmsApi.linkedLookup(targetType, targetId, q).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, targetType, targetId]);
  const attach = async (d) => {
    setBusy(true);
    try {
      await dmsApi.attachLinked(targetType, targetId, d.id);
      toast.success('書類を連携しました');
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || '連携に失敗しました'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="linked-attach-dialog">
        <DialogHeader><DialogTitle className="text-base">ドキュメントから選択</DialogTitle></DialogHeader>
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="タイトル・書類番号で検索"
            className="h-9 pl-8 text-sm" data-testid="linked-attach-search" autoFocus />
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {results.length === 0 && <p className="text-xs text-zinc-400 py-4 text-center">該当する書類がありません</p>}
          {results.map((d) => (
            <button key={d.id} type="button" disabled={busy} onClick={() => attach(d)}
              className="w-full flex items-center gap-2 rounded-lg border border-zinc-200 px-2.5 py-2 text-left hover:bg-zinc-50 transition-colors"
              data-testid={`linked-attach-item-${d.id}`}>
              {docIcon(d.mime_type)}
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium text-zinc-800 truncate">{d.title}</span>
                <span className="block text-[10px] text-zinc-400 font-mono">{d.document_number} · {d.doc_type_label}</span>
              </span>
              <Badge variant="outline" className={`text-[9px] shrink-0 ${STATUS_STYLE[d.status] || ''}`}>{d.status_label}</Badge>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UploadDialog({ open, onClose, targetType, targetId, meta, onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [folderId, setFolderId] = useState('');
  const [docType, setDocType] = useState('other');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setFile(null); setTitle(''); setFolderId(meta?.default_folder_id || ''); setDocType('other'); }
  }, [open, meta]);
  const submit = async () => {
    if (!file) { toast.error('ファイルを選択してください'); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title);
    fd.append('folder_id', folderId);
    fd.append('doc_type', docType);
    setBusy(true);
    try {
      await dmsApi.uploadLinked(targetType, targetId, fd);
      toast.success('アップロードして連携しました');
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'アップロードに失敗しました'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="linked-upload-dialog">
        <DialogHeader><DialogTitle className="text-base">書類をアップロードして連携</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-zinc-600">ファイル</label>
            <Input type="file" className="h-9 mt-1 text-xs" data-testid="linked-upload-file"
              onChange={(e) => { const f = e.target.files?.[0]; setFile(f || null); if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, '')); }} />
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-600">タイトル</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="空欄の場合はファイル名"
              className="h-9 mt-1 text-sm" data-testid="linked-upload-title" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-zinc-600">保存先フォルダー</label>
              <Select value={folderId} onValueChange={setFolderId}>
                <SelectTrigger className="h-9 mt-1 text-xs" data-testid="linked-upload-folder"><SelectValue placeholder="フォルダー" /></SelectTrigger>
                <SelectContent>
                  {(meta?.folders || []).map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-600">書類種別</label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="h-9 mt-1 text-xs" data-testid="linked-upload-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.doc_types || []).map((tp) => <SelectItem key={tp.id} value={tp.id}>{tp.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button onClick={submit} disabled={busy} className="bg-teal-700 hover:bg-teal-800" data-testid="linked-upload-save">
            <UploadSimple size={15} className="mr-1" /> {busy ? 'アップロード中…' : 'アップロード'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LinkedDocsPanel({ targetType, targetId, readOnly = false, className = '' }) {
  const [data, setData] = useState(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    if (!targetId) return;
    dmsApi.linked(targetType, targetId).then(setData).catch(() => setData(null));
  }, [targetType, targetId]);
  useEffect(() => { load(); }, [load]);

  if (!data?.enabled) return null;
  const canEdit = data.can_edit && !readOnly;

  const unlink = async (d) => {
    if (!window.confirm(`「${d.title}」の連携を解除しますか？（書類自体は削除されません）`)) return;
    try {
      await dmsApi.unlinkLinked(targetType, targetId, d.link_id);
      toast.success('連携を解除しました');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '解除に失敗しました'); }
  };

  return (
    <div className={`space-y-2 ${className}`} data-testid="linked-docs-panel">
      <div className="flex items-center gap-2">
        <p className="text-xs font-semibold text-zinc-600 flex items-center gap-1.5">
          <Files size={15} weight="bold" /> 関連書類
          {data.documents.length > 0 && (
            <span className="text-[10px] font-bold text-teal-600 tabular-nums" data-testid="linked-docs-count">{data.documents.length}</span>
          )}
        </p>
        {canEdit && (
          <div className="ml-auto flex items-center gap-1.5">
            <Button size="sm" variant="outline" className="h-7 text-[11px] px-2"
              onClick={() => setAttachOpen(true)} data-testid="linked-docs-attach-btn">
              <Plus size={12} className="mr-1" /> ドキュメントから選択
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 text-teal-700 border-teal-200 hover:bg-teal-50"
              onClick={() => setUploadOpen(true)} data-testid="linked-docs-upload-btn">
              <UploadSimple size={12} className="mr-1" /> アップロード
            </Button>
          </div>
        )}
      </div>
      {data.documents.length === 0 ? (
        <p className="text-[11px] text-zinc-400" data-testid="linked-docs-empty">関連書類はありません</p>
      ) : (
        <div className="space-y-1">
          {data.documents.map((d) => (
            <div key={d.link_id} className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5"
              data-testid={`linked-doc-row-${d.id}`}>
              {docIcon(d.mime_type)}
              <button type="button" onClick={() => navigate(`/documents?open=${d.id}`)}
                className="min-w-0 flex-1 text-left group" data-testid={`linked-doc-open-${d.id}`}>
                <span className="block text-[13px] font-medium text-zinc-800 truncate group-hover:text-teal-700 group-hover:underline">
                  {d.title} <ArrowSquareOut size={11} className="inline text-zinc-300 group-hover:text-teal-600" />
                </span>
                <span className="block text-[10px] text-zinc-400 font-mono truncate">
                  {d.document_number} · {d.doc_type_label} · {fmtSize(d.file_size)} · {fmtDate(d.updated_at)}
                </span>
              </button>
              <Badge variant="outline" className={`text-[9px] shrink-0 ${STATUS_STYLE[d.status] || ''}`}>{d.status_label}</Badge>
              {canEdit && (
                <button type="button" onClick={() => unlink(d)} title="連携を解除"
                  className="text-zinc-300 hover:text-rose-500 transition-colors shrink-0" data-testid={`linked-doc-unlink-${d.id}`}>
                  <LinkBreak size={15} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <AttachDialog open={attachOpen} onClose={() => setAttachOpen(false)}
        targetType={targetType} targetId={targetId} onDone={load} />
      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)}
        targetType={targetType} targetId={targetId} meta={data} onDone={load} />
    </div>
  );
}
